import { Loader2 } from "lucide-react";
import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link, type LinkProps } from "react-router";

import { useOptionalOrganization } from "@/auth/organization";
import { missingPermissionReason } from "@/lib/access";
import type { ResourceType } from "@/lib/types";

import { cx } from "./cx";
import { Tooltip } from "./Tooltip";

/**
 * `primary` is the kit's own look. `brand` is the coral of the redesigned screens (domains, billing,
 * hosting, settings), which is used with the `lg`, `pill` and `block` sizes below.
 */
type Variant = "primary" | "brand" | "secondary" | "danger" | "dangerOutline" | "ghost";
/** `sm`/`md` are fixed-height; `lg` is the design's 10px-radius button, `pill` its rounded one, `block` a full-width one. */
type Size = "sm" | "md" | "lg" | "pill" | "block";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-brand-600 text-white hover:bg-brand-700 disabled:bg-brand-600/60 aria-disabled:bg-brand-600/60",
  brand: "bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-60 aria-disabled:opacity-60",
  secondary: "border border-border bg-surface text-fg hover:bg-muted aria-disabled:text-fg-subtle",
  danger: "bg-red-600 text-white hover:bg-red-700 disabled:bg-red-600/60 aria-disabled:bg-red-600/60",
  dangerOutline:
    "border border-[#ffc4bb] bg-surface text-danger-fg hover:bg-[#fff1ee] disabled:opacity-60 aria-disabled:opacity-60",
  ghost: "text-fg-muted hover:bg-muted hover:text-fg aria-disabled:text-fg-subtle",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 gap-2 px-3 text-sm font-medium rounded-lg",
  md: "h-10 gap-2 px-4 text-sm font-medium rounded-lg",
  lg: "gap-2 rounded-[10px] px-4 py-2.5 text-sm font-semibold whitespace-nowrap",
  pill: "gap-1.5 rounded-full px-4 py-[9px] text-[13px] font-semibold whitespace-nowrap",
  block: "w-full gap-2 rounded-[10px] px-[18px] py-3 text-sm font-semibold",
};

/** Button styling for links (`<Link className={buttonClass()}>`) — never nest a <button> in an <a>. */
export function buttonClass(variant: Variant = "primary", size: Size = "md", className?: string): string {
  return cx(
    "inline-flex items-center justify-center transition-colors disabled:cursor-not-allowed aria-disabled:cursor-not-allowed",
    SIZES[size],
    VARIANTS[variant],
    className,
  );
}

/**
 * A permission an action needs: held across the business (`"domains:manage"`), or on one website or
 * domain folder (`{ permission: "dns:manage", on: ["domain_folder", folderId] }`).
 */
export type Requirement =
  string | { permission: string; on: readonly [ResourceType, string | null | undefined] };

/**
 * Why every permission-gated action inside is unavailable whatever the person's role (a domain CortexGrip
 * support suspended: "This domain is suspended. Contact support."). The server refuses them too.
 */
export const ActionsBlocked = createContext<string | null>(null);

/**
 * Why the signed-in person can't act for lack of a permission, or null when they can (or when there is
 * no business yet, e.g. during sign-in). The server enforces every permission again; this only explains.
 */
export function useMissingPermission(
  requires: Requirement | readonly Requirement[] | undefined,
): string | null {
  const org = useOptionalOrganization();
  const blocked = useContext(ActionsBlocked);
  if (requires == null || !org) return null;
  if (blocked) return blocked;
  const needs =
    typeof requires === "string" || !Array.isArray(requires) ? [requires as Requirement] : requires;
  if (!needs.length) return null;
  if (org.permissionsLoading) return "Checking your access…";
  for (const r of needs) {
    if (typeof r === "string") {
      if (!org.can(r)) return missingPermissionReason(r);
    } else if (!org.canOn(r.permission, r.on[0], r.on[1])) {
      return missingPermissionReason(r.permission, { elsewhere: org.canAny(r.permission) });
    }
  }
  return null;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  /**
   * Use `className` as the whole styling (a page's own pill or icon button) and keep only the behaviour:
   * `requires`, `disabledReason` and `loading`.
   */
  unstyled?: boolean;
  /**
   * Shown but unavailable (QB-057) unless the person holds this permission (or all of these). The
   * button says which role to ask for, on hover, keyboard focus and to screen readers.
   */
  requires?: Requirement | readonly Requirement[];
  /**
   * Why the action is unavailable right now ("Change something to save"). While it is set the button
   * is unavailable and says why. Pass `undefined` once the action is available.
   */
  disabledReason?: ReactNode;
}

/**
 * The shared button. An action that is unavailable for a stated reason (`requires`, `disabledReason`)
 * stays focusable: it is `aria-disabled` rather than natively disabled, its click (and Enter in a form)
 * does nothing, and the reason is its accessible description plus a tooltip. `loading` disables it
 * natively and marks it busy; its label ("Saving…") is the reason.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = "primary",
    size = "md",
    loading = false,
    icon,
    unstyled = false,
    requires,
    disabledReason,
    className,
    children,
    disabled,
    type = "button",
    onClick,
    ...rest
  } = props;
  const reasonId = useId();
  const missing = useMissingPermission(requires);
  const reason: ReactNode = missing ?? (disabledReason || null);
  const blocked = !loading && reason != null;
  // A button that can be blocked keeps its wrapper as the reason comes and goes, so it keeps focus.
  const reasoned = requires != null || "disabledReason" in props;
  const describedBy = [rest["aria-describedby"], blocked && reasonId].filter(Boolean).join(" ") || undefined;

  const btn = (
    <button
      ref={ref}
      type={type}
      disabled={loading || (!!disabled && !blocked)}
      className={
        unstyled
          ? cx(className, "aria-disabled:cursor-not-allowed aria-disabled:opacity-60")
          : buttonClass(variant, size, className)
      }
      {...rest}
      aria-busy={loading || undefined}
      aria-disabled={blocked || rest["aria-disabled"]}
      aria-describedby={describedBy}
      onClick={blocked ? blockClick : onClick}
    >
      {loading && !unstyled ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
  if (!reasoned) return btn;
  return (
    <Tooltip content={blocked ? reason : null} keepWrapper>
      {btn}
      {blocked && (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      )}
    </Tooltip>
  );
});

/** An action that is unavailable for a stated reason does nothing, including when Enter in a form clicks it. */
function blockClick(e: MouseEvent<HTMLButtonElement>) {
  e.preventDefault();
  e.stopPropagation();
}

export interface LinkButtonProps extends Omit<LinkProps, "className"> {
  variant?: Variant;
  size?: Size;
  /** Replaces the variant styling, for links styled by their page (pills, card links). */
  className?: string;
  requires?: ButtonProps["requires"];
  disabledReason?: ReactNode;
}

/**
 * A link styled as a button that is shown but unavailable, with the reason, when the person lacks
 * `requires` (or `disabledReason` is set). Unavailable, it is a focusable `aria-disabled` button, never
 * a link that goes nowhere.
 */
export function LinkButton({
  variant = "primary",
  size = "md",
  className,
  requires,
  disabledReason,
  children,
  ...link
}: LinkButtonProps) {
  const reasonId = useId();
  const missing = useMissingPermission(requires);
  const reason: ReactNode = missing ?? (disabledReason || null);
  const cls = className ?? buttonClass(variant, size);
  if (reason == null) {
    return (
      <Link className={cls} {...link}>
        {children as ReactNode}
      </Link>
    );
  }
  return (
    <Tooltip content={reason}>
      <button
        type="button"
        aria-disabled
        aria-describedby={reasonId}
        onClick={blockClick}
        className={cx(cls, "cursor-not-allowed opacity-60")}
      >
        {children as ReactNode}
      </button>
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </Tooltip>
  );
}
